import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { shareReplay, tap } from 'rxjs/operators';
import { Chain } from '../../interfaces/chain';
import { IRestaurantInfo } from '../../interfaces/restaurant';
import { User } from '../../interfaces/user';
import { AdminService } from '../admin/admin.service';
import { AuthService } from './auth.service';

export interface AuthSession {
  user: User;
  chains: Chain[];
  restaurants: IRestaurantInfo[];
}

@Injectable({
  providedIn: 'root'
})
export class AuthSessionService {
  private session$: Observable<AuthSession> | null = null;
  private tokenKey: string | null = null;

  constructor(
    private authService: AuthService,
    private adminService: AdminService
  ) {}

  public bootstrap(): Observable<AuthSession | null> {
    const tokenKey = this.getTokenKey();

    if (!tokenKey) {
      this.reset();
      return of(null);
    }

    if (!this.session$ || this.tokenKey !== tokenKey) {
      this.tokenKey = tokenKey;
      this.session$ = forkJoin({
        user: this.authService.fetchCurrentUser(),
        chains: this.adminService.getAllChains(),
        restaurants: this.adminService.getAllRestaurants()
      }).pipe(
        tap(({ user, chains, restaurants }) => {
          this.authService.user$.next(user);
          this.authService.chains.next(chains);
          this.authService.restaurants.next(restaurants);
        }),
        shareReplay(1)
      );
    }

    return this.session$;
  }

  public reset(): void {
    this.session$ = null;
    this.tokenKey = null;
  }

  private getTokenKey(): string | null {
    const tokens = this.authService.getTokens();

    if (!tokens) {
      return null;
    }

    return `${tokens.token}.${tokens.refreshToken}.${tokens.savedAt}`;
  }
}
